import { useEffect, useState } from 'react';
import type { Colony, ColonyEvent, ColonyOrigin, EventType, HiveSystem, HoneySuper, Queen, QueenChangeEvent, QueenOrigin, SuperType } from '../../db/model';
import { queenColor, queenMarkColor, type QueenMarkColor } from '../../db/model';
import { read } from '../../db/db';
import { colonyHistory, fmt, naturalLabel, summarize } from '../../db/queries';
import { createSplit, replaceQueen, saveColony, saveQueen, setDeleted } from '../../db/actions';
import { todayISO } from '../../db/rules';
import { back, go, useQuery, useRoute, useSeasonStart } from '../hooks';
import { seasonOf } from '../../db/season';
import { colonyName,
  COLONY_ORIGIN,
  COLONY_STATUS,
  EVENT_LABEL,
  HIVE_SYSTEM,
  QUEEN_CHANGE_REASON,
  QUEEN_COLOR,
  QUEEN_ORIGIN,
  SUPER_TYPE,
  daysAgo,
  fmtDate,
  plural,
} from '../labels';
import { describeEvent } from '../describe';
import { QueenLineField, rememberQueenLine } from '../queen-lines';
import { ageText, queenAge, useQueenAgeLimit } from '../queen-age';
import { Chips, DateInput, Empty, Field, Link, Notice, SaveBar, Screen, Section, Sheet, Stepper, TextInput, YesNo, toast } from '../ui';
import { saved } from '../confirm';
import { ColonyTasks } from '../tasks-ui';
import { EventIcon, Icon } from '../icons';

const MAIN_TYPES: EventType[] = ['inspection', 'treatment', 'feeding', 'harvest'];
const MORE_TYPES: EventType[] = ['super_change', 'varroa_check', 'note', 'swarm', 'move', 'status_change'];

// ---------------------------------------------------------------------------
// Karta včelstva
// ---------------------------------------------------------------------------

export function ColonyScreen({ id }: { id: string }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const seasonStart = useSeasonStart();
  const ageLimit = useQueenAgeLimit();
  const data = useQuery(async () => {
    const colony = await read.get('colonies', id);
    if (!colony) return { colony };
    const [apiaries, colonies, events] = await Promise.all([read.all('apiaries'), read.all('colonies'), colonyHistory(id)]);
    const queen = colony.currentQueenId ? await read.get('queens', colony.currentQueenId) : undefined;
    return { colony, apiaries, colonies, events, queen };
  }, [id]);
  if (!data) return null;
  const { colony } = data;
  if (!colony || colony.deletedAt)
    return (
      <Screen title="Včelstvo nenalezeno" backTo="">
        <Notice tone="warn">Včelstvo z tohoto odkazu v aplikaci uložené není.</Notice>
        <p>
          Na iPhonu otevírá fotoaparát štítky v Safari, které nevidí data aplikace na ploše. Otevřete aplikaci <strong>Úlník</strong> z plochy a
          použijte dole tlačítko <strong>Načíst QR</strong>.
        </p>
        <button className="btn wide" onClick={() => go('skenovat', true)}>
          <Icon name="qr" size={20} /> Načíst QR
        </button>
      </Screen>
    );
  const { apiaries, colonies, events, queen } = data as Required<typeof data>;
  const apiary = apiaries.find((a) => a.id === colony.apiaryId);
  const names = {
    apiary: (aid: string) => apiaries.find((a) => a.id === aid)?.name ?? '?',
    colony: (cid: string) => colonyName(colonies.find((c) => c.id === cid)?.label) ?? '?',
  };
  const summary = summarize(events, todayISO(), seasonStart ?? 1, id);
  const cur = seasonOf(todayISO(), seasonStart ?? 1);
  const period = (seasonStart ?? 1) === 1 ? 'letos' : cur.label;
  const parent = colony.parentColonyId ? colonies.find((c) => c.id === colony.parentColonyId) : undefined;
  const newEvent = (t: EventType) => go(`zapis/novy?typ=${t}&vcelstva=${id}&stanoviste=${colony.apiaryId}`);

  return (
    <Screen
      title={`Včelstvo ${colonyName(colony.label)}`}
      crumbs={[
        ['Včelnice', ''],
        // bez platného stanoviště (přesun bez cíle ve starší verzi) – drobeček by vedl na „nenalezeno“
        ...(apiary ? ([[apiary.name, `stanoviste/${colony.apiaryId}`]] as [string, string][]) : []),
      ]}
      subtitle={[
        `původ: ${(COLONY_ORIGIN[colony.origin] ?? COLONY_ORIGIN.unknown).toLowerCase()}${colony.originDate ? ' ' + fmtDate(colony.originDate) : ''}${parent ? ` z včelstva ${colonyName(parent.label)}` : ''}`,
      ]
        .filter(Boolean)
        .join(' · ')}
      backTo={apiary ? `stanoviste/${colony.apiaryId}` : ''}
      actions={
        <Link to={`vcelstvo/${id}/upravit`} className="bar-btn">
          Upravit
        </Link>
      }
    >
      {colony.status === 'active' && (!apiary || apiary.archived) && (
        <Notice tone="warn">
          {apiary ? `Stanoviště ${apiary.name} je zrušené` : 'Včelstvo nemá stanoviště'} – v seznamech včelstev ho proto nevidíte.{' '}
          <Link to={`zapis/novy?typ=move&vcelstva=${id}`}>Přesunout na stanoviště</Link>
        </Notice>
      )}
      {colony.status !== 'active' && (
        <Notice tone="warn">
          {COLONY_STATUS[colony.status]}
          {colony.statusDate ? ` ${fmtDate(colony.statusDate)}` : ''}
        </Notice>
      )}

      {colony.status === 'active' && (
        <ColonyTasks colony={colony} events={events} queen={queen} />
      )}

      <div className="type-grid main">
        {MAIN_TYPES.map((t) => (
          <button key={t} className="type-btn" onClick={() => newEvent(t)}>
            <span className="type-icon">
              <EventIcon type={t} size={30} />
            </span>
            {EVENT_LABEL[t]}
          </button>
        ))}
      </div>
      <button className="btn wide" onClick={() => setMoreOpen(true)}>
        Další zápis…
      </button>

      <div className="info-grid">
        <Link to={`vcelstvo/${id}/upravit/matka`} className="info tile-link">
          <div className="info-label">Matka</div>
          {queen ? (
            <div className="info-value">
              <span className="queen-dot" style={{ background: QUEEN_COLOR[queenColor(queen)]?.css }} /> {queen.year}
              <div className="muted small">
                {[queen.marked ? 'značená' : 'neznačená', queen.clipped ? 'střižená' : null, queen.line].filter(Boolean).join(', ')}
              </div>
              {!!ageLimit && queenAge(queen.year) >= ageLimit && (
                <div className="queen-age-warn">
                  <Icon name="warning" size={15} /> stáří {ageText(queenAge(queen.year))} – zvažte výměnu</div>
              )}
            </div>
          ) : (
            <div className="info-value muted">neevidována</div>
          )}
          <span className="tile-chev">›</span>
        </Link>
        <Link to={`vcelstvo/${id}/upravit/plodiste`} className="info tile-link">
          <div className="info-label">Plodiště</div>
          <div className="info-value">
            {colony.boxCount != null ? plural(colony.boxCount, 'nástavek', 'nástavky', 'nástavků') : '–'}
            <div className="muted small">{HIVE_SYSTEM[colony.hiveSystem]}</div>
          </div>
          <span className="tile-chev">›</span>
        </Link>
      </div>
      <SupersCard colonyId={id} supers={colony.supers ?? []} />
      <div className="info-grid">
        <Link to={`vcelstvo/${id}/historie?filtr=harvest`} className="info tile-link">
          <div className="info-label">Med {period}</div>
          <div className="info-value">
            {fmt(summary.harvestKgThisYear)} kg
            <div className="muted small">
              {summary.harvestsThisYear > 0 ? `${summary.harvestsThisYear}× medobraní` : 'letos bez medobraní'}
            </div>
            {summary.lastHarvestDate && (
              <div className="muted small">
                naposledy <span className="nowrap">{fmtDate(summary.lastHarvestDate)}</span>
              </div>
            )}
          </div>
          <span className="tile-chev">›</span>
        </Link>
        <Link to={`vcelstvo/${id}/historie?filtr=treatment`} className="info tile-link">
          <div className="info-label">Léčení {period}</div>
          <div className="info-value">
            {summary.treatmentsThisYear === 0 ? '–' : `${summary.treatmentsThisYear}×`}
            {summary.lastTreatment && (
              <div className="muted small">
                naposledy <span className="nowrap">{fmtDate(summary.lastTreatment.date)}</span>
                <br />
                {summary.lastTreatment.product}
              </div>
            )}
          </div>
          <span className="tile-chev">›</span>
        </Link>
        <Link to={`vcelstvo/${id}/historie?filtr=feeding`} className="info tile-link">
          <div className="info-label">Krmení {period}</div>
          <div className="info-value">
            {summary.feedingsThisYear === 0
              ? '–'
              : `${fmt(summary.feedSugarKgThisYear)} kg cukru`}
            {summary.feedingsThisYear > 0 && (
              <div className="muted small">
                {summary.feedingsThisYear}× krmeno
              </div>
            )}
            {summary.lastFeedingDate && (
              <div className="muted small">
                naposledy <span className="nowrap">{fmtDate(summary.lastFeedingDate)}</span>
              </div>
            )}
          </div>
          <span className="tile-chev">›</span>
        </Link>
        <Link to={`vcelstvo/${id}/historie?filtr=varroa`} className="info tile-link">
          <div className="info-label">Varroáza</div>
          <div className={'info-value' + (summary.lastVarroa?.alarm ? ' alarm' : '')}>
            {summary.lastVarroa ? fmtDate(summary.lastVarroa.date) : '–'}
            <div className="muted small">
              {!summary.lastVarroa
                ? 'zatím bez kontroly'
                : summary.lastVarroa.fall
                  ? `celkový spad: ${plural(summary.lastVarroa.miteCount, 'roztoč', 'roztoči', 'roztočů')}`
                  : summary.lastVarroa.text}
            </div>
          </div>
          <span className="tile-chev">›</span>
        </Link>
      </div>

      <Link to={`vcelstvo/${id}/rok`} className="info history-tile">
        <div className="info-label">Roční přehled</div>
        <div className="info-value">{cur.label}</div>
        <div className="muted small">souhrn roku a porovnání s předchozími roky</div>
        <span className="chev">›</span>
      </Link>
      <Link to={`vcelstvo/${id}/historie`} className="info history-tile">
        <div className="info-label">Historie</div>
        <div className="info-value">{events.length === 0 ? 'zatím žádné zápisy' : plural(events.length, 'zápis', 'zápisy', 'zápisů')}</div>
        {events[0] && (
          <div className="muted small">
            Naposledy: {EVENT_LABEL[events[0].type]} · {fmtDate(events[0].date)}
          </div>
        )}
        <span className="chev">›</span>
      </Link>

      <Sheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Další zápis">
        <div className="type-grid">
          <button className="type-btn" onClick={() => go(`vcelstvo/${id}/matka`)}>
            <span className="type-icon">
              <EventIcon type="queen_change" size={30} />
            </span>
            {EVENT_LABEL.queen_change}
          </button>
          <button className="type-btn" onClick={() => go(`vcelstvo/${id}/oddelek`)}>
            <span className="type-icon">
              <EventIcon type="split" size={30} />
            </span>
            {EVENT_LABEL.split}
          </button>
          {MORE_TYPES.map((t) => (
            <button
              key={t}
              className="type-btn"
              onClick={() => {
                setMoreOpen(false);
                newEvent(t);
              }}
            >
              <span className="type-icon">
              <EventIcon type={t} size={30} />
            </span>
              {EVENT_LABEL[t]}
            </button>
          ))}
        </div>
      </Sheet>
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Historie včelstva (celý seznam zápisů + původ)
// ---------------------------------------------------------------------------

const HISTORY_FILTERS: [string, string, EventType[] | null][] = [
  ['all', 'Vše', null],
  ['inspection', 'Prohlídky', ['inspection']],
  ['treatment', 'Léčení', ['treatment']],
  ['varroa', 'Varroáza', ['varroa_check']],
  ['feeding', 'Krmení', ['feeding']],
  ['harvest', 'Medobraní', ['harvest']],
  ['supers', 'Medníky', ['super_change']],
  ['queen', 'Matka', ['queen_change', 'split', 'swarm']],
  ['other', 'Ostatní', ['move', 'status_change', 'note']],
];

/** Který nový zápis nabídnout u daného filtru. */
const FILTER_ADD: Record<string, EventType> = {
  inspection: 'inspection',
  treatment: 'treatment',
  varroa: 'varroa_check',
  feeding: 'feeding',
  harvest: 'harvest',
  supers: 'super_change',
};

export function ColonyHistoryScreen({ id }: { id: string }) {
  const { query } = useRoute();
  const [filter, setFilter] = useState(query.get('filtr') ?? 'all');
  // otevřeno z dlaždice (Varroáza, Med, Krmení, Léčení): jen zápisy tohoto druhu, nic jiného
  const locked = query.has('filtr');
  const data = useQuery(async () => {
    const colony = await read.get('colonies', id);
    const [apiaries, colonies, events] = await Promise.all([read.all('apiaries'), read.all('colonies'), colonyHistory(id)]);
    return { colony, apiaries, colonies, events };
  }, [id]);
  if (!data?.colony) return null;
  const { colony, apiaries, colonies, events } = data;
  const names = {
    apiary: (aid: string) => apiaries.find((a) => a.id === aid)?.name ?? '?',
    colony: (cid: string) => colonyName(colonies.find((c) => c.id === cid)?.label) ?? '?',
  };
  const parent = colony.parentColonyId ? colonies.find((c) => c.id === colony.parentColonyId) : undefined;
  const types = HISTORY_FILTERS.find(([k]) => k === filter)?.[2];
  const shown = types ? events.filter((e) => types.includes(e.type)) : events;
  const options = HISTORY_FILTERS.filter(([k, , t]) => !t || k === filter || events.some((e) => t.includes(e.type))).map(
    ([k, label, t]) => [k, t ? `${label} (${events.filter((e) => t.includes(e.type)).length})` : `${label} (${events.length})`] as [string, string],
  );

  return (
    <Screen
      title={filter === 'all' ? 'Historie' : HISTORY_FILTERS.find(([k]) => k === filter)?.[1] ?? 'Historie'}
      crumbs={[
        [names.apiary(colony.apiaryId), `stanoviste/${colony.apiaryId}`],
        [colonyName(colony.label), `vcelstvo/${id}`],
      ]}
      backTo={`vcelstvo/${id}`}
    >
      {!locked && (
      <div className="info">
        <div className="info-label">Původ</div>
        <div className="info-value">
          {COLONY_ORIGIN[colony.origin]}
          {colony.originDate ? ` · ${fmtDate(colony.originDate)}` : ''}
        </div>
        {parent && (
          <div className="small">
            z <Link to={`vcelstvo/${parent.id}`}>včelstva {colonyName(parent.label)}</Link>
          </div>
        )}
        {colony.note && <div className="small">{colony.note}</div>}
      </div>
      )}

      {!locked && events.length > 0 && <Chips ariaLabel="Filtr historie" options={options} value={filter} onChange={(v) => v && setFilter(v)} />}
      {FILTER_ADD[filter] && (
        <button
          className="btn primary wide"
          onClick={() => go(`zapis/novy?typ=${FILTER_ADD[filter]}&vcelstva=${id}${filter === 'supers' ? '&akce=add' : ''}`)}
        >
          + {EVENT_LABEL[FILTER_ADD[filter]]}
        </button>
      )}

      {shown.length === 0 ? (
        <Empty>{locked ? `Zatím žádný zápis – ${(HISTORY_FILTERS.find(([k]) => k === filter)?.[1] ?? '').toLowerCase()}.` : 'Zatím žádné zápisy.'}</Empty>
      ) : (
        <ol className="timeline">
          {shown.map((e) => (
            <li key={e.id}>
              <Link to={`zapis/${e.id}`} className="tl-item">
                <span className="tl-icon">
                  <EventIcon type={e.type} size={22} />
                </span>
                <span className="tl-body">
                  <span className="tl-title">
                    {EVENT_LABEL[e.type]} <span className="muted small">{fmtDate(e.date)}</span>
                  </span>
                  <span className="tl-text">{describeEvent(e, names)}</span>
                  {e.note && <span className="tl-note">{e.note}</span>}
                </span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Medníky na kartě včelstva
// ---------------------------------------------------------------------------

function SupersCard({ colonyId, supers }: { colonyId: string; supers: HoneySuper[] }) {
  const sorted = [...supers].sort((a, b) => (a.addedAt ?? '').localeCompare(b.addedAt ?? ''));
  const counts = (Object.keys(SUPER_TYPE) as SuperType[])
    .map((t) => [t, supers.filter((x) => x.type === t).length] as const)
    .filter(([, n]) => n > 0);
  return (
    <Link to={`vcelstvo/${colonyId}/upravit/medniky`} className="info supers tile-link">
      <div className="supers-head">
        <div>
          <div className="info-label">Medníky</div>
          <div className="info-value">
            {supers.length === 0 ? 'žádné' : counts.map(([t, n]) => `${n}× ${SUPER_TYPE[t].toLowerCase()}`).join(', ')}
          </div>
        </div>
      </div>
      <span className="tile-chev">›</span>
      {sorted.length > 0 && (
        <ul className="supers-list">
          {sorted.map((x, i) => (
            <li key={x.id}>
              <span className="supers-n">{i + 1}.</span> {SUPER_TYPE[x.type]}
              <span className="muted small">
                {' '}
                · nasazen {fmtDate(x.addedAt)} ({daysAgo(x.addedAt)})
              </span>
            </li>
          ))}
        </ul>
      )}
    </Link>
  );
}

const SUPER_OPTIONS = Object.entries(SUPER_TYPE) as [SuperType, string][];

/** Ruční oprava seznamu medníků (bez zápisu do historie). */
function SupersEditor({ supers, onChange }: { supers: HoneySuper[]; onChange: (s: HoneySuper[]) => void }) {
  const upd = (i: number, p: Partial<HoneySuper>) => onChange(supers.map((x, j) => (j === i ? { ...x, ...p } : x)));
  return (
    <>
      {supers.length === 0 && <p className="muted small">Žádné medníky.</p>}
      {supers.map((x, i) => (
        <div key={x.id} className="super-edit">
          <div className="row">
            <strong>{i + 1}. medník</strong>
            <button type="button" className="btn small danger" onClick={() => onChange(supers.filter((_, j) => j !== i))}>
              Odebrat
            </button>
          </div>
          <Chips ariaLabel={`Typ medníku ${i + 1}`} options={SUPER_OPTIONS} value={x.type} onChange={(v) => v && upd(i, { type: v })} />
          <Field label="Nasazen">
            <DateInput value={x.addedAt} max={todayISO()} onChange={(v) => upd(i, { addedAt: v })} />
          </Field>
        </div>
      ))}
      <button
        type="button"
        className="btn small"
        onClick={() => onChange([...supers, { id: crypto.randomUUID(), type: supers[supers.length - 1]?.type ?? 'low', addedAt: todayISO() }])}
      >
        + Přidat medník
      </button>
    </>
  );
}

// ---------------------------------------------------------------------------
// Formulář včelstva (při založení i s matkou)
// ---------------------------------------------------------------------------

const HIVE_OPTIONS = Object.entries(HIVE_SYSTEM) as [HiveSystem, string][];
const ORIGIN_OPTIONS = Object.entries(COLONY_ORIGIN) as [ColonyOrigin, string][];
const QUEEN_ORIGIN_OPTIONS = Object.entries(QUEEN_ORIGIN) as [QueenOrigin, string][];

export type ColonySection = 'zakladni' | 'plodiste' | 'medniky' | 'matka';
const SECTION_TITLE: Record<ColonySection, string> = { zakladni: 'Základní údaje', plodiste: 'Plodiště', medniky: 'Medníky', matka: 'Matka' };

export function ColonyForm({ id, apiaryId, section }: { id?: string; apiaryId?: string; section?: ColonySection }) {
  const show = (k: ColonySection) => !section || section === k;
  const [form, setForm] = useState<Partial<Colony> | null>(null);
  const [queen, setQueen] = useState<Partial<Queen>>({ year: new Date().getFullYear(), marked: false, clipped: false, origin: 'unknown' });
  const [withQueen, setWithQueen] = useState(!id);
  const apiaries = useQuery(() => read.all('apiaries'), []);

  useEffect(() => {
    if (id) {
      (async () => {
        const c = await read.get('colonies', id);
        const q = c?.currentQueenId ? await read.get('queens', c.currentQueenId) : undefined;
        if (q) {
          setQueen(q);
          setWithQueen(true);
        }
        setForm(c ?? {});
      })();
      return;
    }
    // nové včelstvo: předvyplnit podle ostatních na stanovišti
    (async () => {
      const others = apiaryId ? (await read.byIndex('colonies', 'apiaryId', apiaryId)).sort(naturalLabel) : [];
      const last = others[others.length - 1];
      const nextNum = others.reduce((m, c) => Math.max(m, parseInt(c.label) || 0), 0) + 1;
      setForm({
        apiaryId,
        label: String(nextNum),
        hiveSystem: last?.hiveSystem ?? 'adamec',
        frameSize: last?.frameSize,
        boxCount: 2,
        status: 'active',
        origin: 'unknown',
      });
    })();
  }, [id, apiaryId]);

  if (!form || !apiaries) return null;
  const set = (p: Partial<Colony>) => setForm({ ...form, ...p });

  const save = async () => {
    if (!form.label?.trim()) return toast('Vyplňte číslo úlu.');
    if (!form.apiaryId) return toast('Vyberte stanoviště.');
    if (form.boxCount != null && !(Number.isFinite(form.boxCount) && form.boxCount >= 1 && form.boxCount <= 20))
      return toast('Počet plodišťových nástavků musí být 1 až 20.');
    if (form.originDate && form.originDate > todayISO()) return toast('Datum vzniku nemůže být v budoucnosti.');
    const clash = (await read.byIndex('colonies', 'apiaryId', form.apiaryId)).find(
      (c) => c.label === form.label!.trim() && c.id !== id && c.status === 'active',
    );
    if (clash) return toast(`Číslo ${form.label} už na stanovišti je.`);
    let rec = await saveColony({ ...(form as Colony), label: form.label.trim() });
    if (withQueen && queen.year) {
      await rememberQueenLine(queen.line);
      if (queen.id) {
        await saveQueen(queen as Queen); // úprava stávající matky
      } else {
        const q = await saveQueen({
          ...(queen as Queen),
          colonyId: rec.id,
          status: 'present',
          introducedAt: form.originDate,
        });
        rec = await saveColony({ ...rec, currentQueenId: q.id });
      }
    }
    saved();
    if (id) back(`vcelstvo/${id}`);
    else go(`vcelstvo/${rec.id}`, true);
  };

  return (
    <Screen
      title={section ? SECTION_TITLE[section] : id ? 'Upravit včelstvo' : 'Nové včelstvo'}
      crumbs={id && form.label ? [[colonyName(form.label), `vcelstvo/${id}`]] : undefined}
      backTo={id ? `vcelstvo/${id}` : `stanoviste/${apiaryId}`}
    >
      {show('zakladni') && (
        <Field label="Číslo úlu" htmlFor="c-label">
          <TextInput id="c-label" value={form.label} onChange={(v) => set({ label: v })} />
        </Field>
      )}
      {show('plodiste') && (
        <>
          <Field label="Plodišťové nástavky">
            <Stepper ariaLabel="Počet plodišťových nástavků" value={form.boxCount} min={1} max={8} onChange={(v) => set({ boxCount: v })} />
          </Field>
          <Field label="Úlový systém / rámková míra">
            <Chips options={HIVE_OPTIONS} value={form.hiveSystem} onChange={(v) => v && set({ hiveSystem: v })} />
          </Field>
          <Field label="Rámková míra – upřesnění" htmlFor="c-frame">
            <TextInput id="c-frame" value={form.frameSize} onChange={(v) => set({ frameSize: v })} placeholder="např. 39 × 24, 2 nástavky po 10 rámcích" />
          </Field>
        </>
      )}
      {show('zakladni') && (
        <>
          <Field label="Stanoviště" htmlFor="c-apiary">
            <select id="c-apiary" value={form.apiaryId ?? ''} onChange={(e) => set({ apiaryId: e.target.value })}>
              <option value="">– vyberte –</option>
              {apiaries
                .filter((a) => !a.archived || a.id === form.apiaryId)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Původ">
            <Chips options={ORIGIN_OPTIONS} value={form.origin} onChange={(v) => v && set({ origin: v })} />
          </Field>
          <Field label="Datum vzniku / pořízení" htmlFor="c-od">
            <DateInput id="c-od" optional value={form.originDate} max={todayISO()} onChange={(v) => set({ originDate: v || undefined })} />
          </Field>
        </>
      )}

      {show('medniky') && (
        <Section title={section ? undefined : 'Medníky'}>
          {id && (
            <div className="quick-row">
              <button type="button" className="btn primary" onClick={() => go(`zapis/novy?typ=super_change&akce=add&vcelstva=${id}`)}>
                + Nasadit medník
              </button>
              {(form.supers?.length ?? 0) > 0 && (
                <button type="button" className="btn" onClick={() => go(`zapis/novy?typ=super_change&akce=remove&vcelstva=${id}`)}>
                  − Odebrat medník
                </button>
              )}
            </div>
          )}
          <p className="muted small">Nasazení a odebrání tlačítky výše se zapíše do historie. V seznamu níže jde opravit typ nebo datum.</p>
          <SupersEditor supers={form.supers ?? []} onChange={(supers) => set({ supers })} />
        </Section>
      )}

      {show('matka') && (
        <Section title={section ? undefined : 'Matka'}>
          {queen.id ? (
            <p className="muted small">Úprava údajů současné matky. Když matku měníte za novou, použijte na kartě včelstva zápis „Výměna matky“.</p>
          ) : (
            <label className="check">
              <input type="checkbox" checked={withQueen} onChange={(e) => setWithQueen(e.target.checked)} />
              Evidovat matku
            </label>
          )}
          {withQueen && <QueenFields queen={queen} onChange={setQueen} />}
        </Section>
      )}

      {show('zakladni') && (
        <Field label="Poznámka" htmlFor="c-note">
          <TextInput id="c-note" multiline value={form.note} onChange={(v) => set({ note: v })} />
        </Field>
      )}

      {id && !section && (
        <Section>
          <button
            className="btn danger"
            onClick={async () => {
              await setDeleted('colonies', id, true);
              saved('Včelstvo smazáno', { label: 'Vrátit', run: () => setDeleted('colonies', id, false) });
              go(`stanoviste/${form.apiaryId}`, true);
            }}
          >
            Smazat včelstvo
          </button>
          <p className="muted small">Uhynulé nebo prodané včelstvo nemažte – zapište „Změna stavu“, ať zůstane historie.</p>
        </Section>
      )}
      <SaveBar onSave={save} />
    </Screen>
  );
}

const COLOR_OPTIONS = (Object.keys(QUEEN_COLOR) as QueenMarkColor[]).map((c) => [c, QUEEN_COLOR[c].label] as [QueenMarkColor, string]);

function QueenFields({ queen, onChange }: { queen: Partial<Queen>; onChange: (q: Partial<Queen>) => void }) {
  const set = (p: Partial<Queen>) => onChange({ ...queen, ...p });
  const y = new Date().getFullYear();
  const years = [y, y - 1, y - 2, y - 3, y - 4];
  const byYear = queen.year ? queenMarkColor(queen.year) : undefined;
  const shown = queen.markColor ?? byYear;
  return (
    <>
      <Field label="Rok líhnutí">
        <Chips
          options={years.map((n) => [n, String(n)] as [number, string])}
          value={queen.year}
          onChange={(v) => v && set({ year: v })}
        />
        <div className="row">
          <span className="muted small">jiný rok</span>
          <Stepper ariaLabel="Rok líhnutí" value={queen.year} min={1990} max={y} onChange={(v) => v && v >= 1990 && v <= y && set({ year: v })} />
        </div>
      </Field>
      <YesNo label="Značená" value={queen.marked} onChange={(v) => set({ marked: !!v })} />
      {queen.marked && (
        <Field
          label="Barva značky"
          hint={
            <>
              {shown && <span className="queen-dot" style={{ background: QUEEN_COLOR[shown]?.css }} />}{' '}
              {byYear && (queen.markColor && queen.markColor !== byYear ? `Podle roku by byla ${QUEEN_COLOR[byYear].label}.` : 'Podle roku líhnutí.')}
            </>
          }
        >
          <Chips
            ariaLabel="Barva značky"
            options={COLOR_OPTIONS}
            value={shown}
            onChange={(v) => v && set({ markColor: v === byYear ? undefined : v })}
          />
        </Field>
      )}
      <YesNo label="Střižená křídla" value={queen.clipped} onChange={(v) => set({ clipped: !!v })} />
      <Field label="Původ matky">
        <Chips options={QUEEN_ORIGIN_OPTIONS} value={queen.origin} onChange={(v) => v && set({ origin: v })} />
      </Field>
      <QueenLineField value={queen.line} onChange={(v) => set({ line: v })} />
      <Field label="Chovatel" htmlFor="q-breeder">
        <TextInput id="q-breeder" value={queen.breeder} onChange={(v) => set({ breeder: v })} />
      </Field>
      <Field label="Poznámka k matce" htmlFor="q-note">
        <TextInput id="q-note" value={queen.note} onChange={(v) => set({ note: v })} />
      </Field>
    </>
  );
}

// ---------------------------------------------------------------------------
// Výměna matky
// ---------------------------------------------------------------------------

const REASONS = Object.entries(QUEEN_CHANGE_REASON) as [NonNullable<QueenChangeEvent['reason']>, string][];

export function QueenChangeForm({ colonyId }: { colonyId: string }) {
  const colony = useQuery(() => read.get('colonies', colonyId), [colonyId]);
  const [date, setDate] = useState(todayISO());
  const [reason, setReason] = useState<QueenChangeEvent['reason']>('requeening');
  const [hasNew, setHasNew] = useState(true);
  const [note, setNote] = useState('');
  const [queen, setQueen] = useState<Partial<Queen>>({ year: new Date().getFullYear(), marked: true, clipped: false, origin: 'purchased' });
  if (!colony) return null;
  const save = async () => {
    if (hasNew) await rememberQueenLine(queen.line);
    await replaceQueen({
      colonyId,
      apiaryId: colony.apiaryId,
      date,
      reason,
      note: note || undefined,
      newQueen: hasNew ? ({ ...queen, status: 'present' } as Omit<Queen, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt'>) : null,
    });
    saved();
    back(`vcelstvo/${colonyId}`);
  };
  return (
    <Screen title="Výměna matky" crumbs={[[colonyName(colony.label), `vcelstvo/${colonyId}`]]} backTo={`vcelstvo/${colonyId}`}>
      <Field label="Datum" htmlFor="qc-date">
        <DateInput id="qc-date" value={date} max={todayISO()} onChange={setDate} />
      </Field>
      <Field label="Důvod">
        <Chips options={REASONS} value={reason} onChange={(v) => setReason(v)} allowNone />
      </Field>
      <label className="check">
        <input type="checkbox" checked={hasNew} onChange={(e) => setHasNew(e.target.checked)} />
        Přidána nová matka
      </label>
      {hasNew && <QueenFields queen={queen} onChange={setQueen} />}
      <Field label="Poznámka" htmlFor="qc-note">
        <TextInput id="qc-note" multiline value={note} onChange={setNote} />
      </Field>
      <SaveBar onSave={save} />
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Oddělek
// ---------------------------------------------------------------------------

export function SplitForm({ colonyId }: { colonyId: string }) {
  const data = useQuery(async () => {
    const colony = await read.get('colonies', colonyId);
    const apiaries = (await read.all('apiaries')).filter((a) => !a.archived);
    const all = await read.all('colonies');
    return { colony, apiaries, all };
  }, [colonyId]);
  const [date, setDate] = useState(todayISO());
  const [apiaryId, setApiaryId] = useState<string>();
  const [label, setLabel] = useState<string>();
  const [frames, setFrames] = useState<number | undefined>(3);
  const [note, setNote] = useState('');
  if (!data?.colony) return null;
  const { colony, apiaries, all } = data;
  const target = apiaryId ?? colony.apiaryId;
  const suggested = String(all.filter((c) => c.apiaryId === target).reduce((m, c) => Math.max(m, parseInt(c.label) || 0), 0) + 1);
  const save = async () => {
    const lab = (label ?? suggested).trim();
    if (!lab) return toast('Vyplňte číslo úlu.');
    const clash = (await read.byIndex('colonies', 'apiaryId', target)).find((c) => c.label === lab && c.status === 'active');
    if (clash) return toast(`Číslo ${lab} už na stanovišti je.`);
    const created = await createSplit({ parentId: colonyId, apiaryId: target, label: lab, date, framesTaken: frames, note: note || undefined });
    saved(`Oddělek ${colonyName(created.label)} založen`);
    go(`vcelstvo/${created.id}`, true);
  };
  return (
    <Screen title="Nový oddělek" crumbs={[[colonyName(colony.label), `vcelstvo/${colonyId}`]]} backTo={`vcelstvo/${colonyId}`}>
      <Field label="Datum" htmlFor="s-date">
        <DateInput id="s-date" value={date} max={todayISO()} onChange={setDate} />
      </Field>
      <Field label="Stanoviště oddělku" htmlFor="s-ap">
        <select id="s-ap" value={target} onChange={(e) => setApiaryId(e.target.value)}>
          {apiaries.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </Field>
      <div className="row two">
        <Field label="Číslo úlu oddělku" htmlFor="s-label">
          <TextInput id="s-label" value={label ?? suggested} onChange={setLabel} />
        </Field>
        <Field label="Odebráno plástů">
          <Stepper ariaLabel="Odebráno plástů" value={frames} onChange={setFrames} max={20} />
        </Field>
      </div>
      <Field label="Poznámka" htmlFor="s-note">
        <TextInput id="s-note" multiline value={note} onChange={setNote} placeholder="např. s matečníkem, bez matky…" />
      </Field>
      <SaveBar onSave={save} label="Založit oddělek" />
    </Screen>
  );
}
